import type { TranslationJobScope } from '@/shared/constants/status'
import { useCallback, useEffect, useRef } from 'react'
import { useLocalizedTranslationJobController } from './use-localized-translation-job-controller'

interface UseCreateTranslationDraftOptions<TDraft> {
  /** Bật lưu và khôi phục bản nháp; truyền false nếu màn hình chỉ cần quản lý job dịch. */
  enabled: boolean
  /** Nhóm job dịch, dùng để phân biệt bản nháp của từng loại dữ liệu. */
  scope: TranslationJobScope
  /** Id ổn định của dữ liệu hiện tại, dùng để tìm đúng bản nháp và job dịch. */
  entityId: string
  /** Cấu hình tạo audio cho job dịch; hook truyền tiếp xuống controller bên dưới. */
  audioEnabled: boolean
  /**
   * Dấu hiệu cho biết form cần khởi tạo lại.
   *
   * Đổi giá trị này khi dữ liệu mặc định đổi, ví dụ đổi tenant,
   * danh sách ngôn ngữ, hoặc thứ tự mặc định của câu hỏi/câu trả lời survey.
   */
  initSignature: string
  /** Dữ liệu form hiện tại, sẽ được lưu khi form có nội dung đáng giữ lại. */
  draft: TDraft
  /** Cho biết có nên lưu bản nháp không; false sẽ xóa bản nháp đang có. */
  hasContent: boolean
  /** Đổ dữ liệu bản nháp vào form; undefined nghĩa là đưa form về dữ liệu mặc định. */
  onHydrate: (draft?: TDraft) => void
}

interface UseCreateTranslationDraftResult {
  /** Xóa bản nháp của dữ liệu hiện tại nhưng giữ nguyên job dịch. */
  clearCurrentDraft: () => void
  /** Xóa toàn bộ job dịch và bản nháp của dữ liệu hiện tại. */
  clearCurrentJobs: () => void
}

interface DraftFlowState<TDraft> {
  hydratedSignature: string | null
  hydratedDraft: TDraft | undefined
  draftSavedByThisHook: TDraft | undefined
  draftSavedByThisHookSignature: string | null
  hasDraftSavedByThisHook: boolean
  skipPersistOnceAfterHydrate: boolean
}

/**
 * Lưu và khôi phục bản nháp cho các màn hình tạo mới có phần dịch nội dung.
 *
 * Component cha vẫn giữ state của form. Hook này chỉ phụ trách đồng bộ state đó
 * với store lưu bản nháp và job dịch.
 *
 * ```text
 * mở màn hình hoặc dữ liệu mặc định đổi
 *   -> lấy bản nháp đang có trong store
 *   -> gọi onHydrate(bản nháp hoặc undefined)
 *   -> bỏ qua 1 lần lưu ngay sau đó để không lưu nhầm state mặc định
 * người dùng sửa form
 *   -> có nội dung thì lưu bản nháp
 *   -> không còn nội dung thì xóa bản nháp
 * bấm Mở từ background jobs panel
 *   -> store có bản nháp mới
 *   -> gọi onHydrate(bản nháp mới)
 * tạo thành công
 *   -> component cha gọi clearCurrentDraft() hoặc clearCurrentJobs()
 * ```
 *
 * Cách dùng:
 * 1. Tạo state form ở component cha như bình thường.
 * 2. Gom state hiện tại thành `draft` bằng `useMemo`.
 * 3. Tạo `onHydrate` bằng `useCallback` để đổ bản nháp vào form.
 * 4. Truyền `initSignature`; đổi nó khi dữ liệu mặc định của form đổi.
 * 5. Sau khi tạo thành công, gọi `clearCurrentDraft` hoặc `clearCurrentJobs`.
 */
export function useCreateTranslationDraft<TDraft>({
  enabled,
  scope,
  entityId,
  audioEnabled,
  initSignature,
  draft,
  hasContent,
  onHydrate,
}: UseCreateTranslationDraftOptions<TDraft>): UseCreateTranslationDraftResult {
  const { drafts, setDraft, clearDraft, clearEntityJobs, draftKey } =
    useLocalizedTranslationJobController({
      scope,
      entityId,
      audioEnabled,
    })
  const persistedDraft = drafts[draftKey] as TDraft | undefined
  const draftFlowRef = useRef<DraftFlowState<TDraft>>({
    hydratedSignature: null,
    hydratedDraft: undefined,
    draftSavedByThisHook: undefined,
    draftSavedByThisHookSignature: null,
    hasDraftSavedByThisHook: false,
    skipPersistOnceAfterHydrate: true,
  })

  useEffect(() => {
    const draftFlow = draftFlowRef.current
    if (!enabled) {
      draftFlow.skipPersistOnceAfterHydrate = true
      return
    }

    const alreadyHydrated =
      draftFlow.hydratedSignature === initSignature && draftFlow.hydratedDraft === persistedDraft
    if (alreadyHydrated) {
      return
    }

    const draftCameFromThisHook =
      draftFlow.hasDraftSavedByThisHook &&
      draftFlow.draftSavedByThisHookSignature === initSignature &&
      draftFlow.draftSavedByThisHook === persistedDraft
    if (draftCameFromThisHook) {
      draftFlow.hydratedSignature = initSignature
      draftFlow.hydratedDraft = persistedDraft
      return
    }

    draftFlow.hydratedSignature = initSignature
    draftFlow.hydratedDraft = persistedDraft
    draftFlow.skipPersistOnceAfterHydrate = true
    onHydrate(persistedDraft)
  }, [enabled, initSignature, onHydrate, persistedDraft])

  useEffect(() => {
    if (!enabled) {
      return
    }

    const draftFlow = draftFlowRef.current
    if (draftFlow.skipPersistOnceAfterHydrate) {
      draftFlow.skipPersistOnceAfterHydrate = false
      return
    }

    draftFlow.hasDraftSavedByThisHook = true
    draftFlow.draftSavedByThisHookSignature = initSignature
    if (!hasContent) {
      draftFlow.draftSavedByThisHook = undefined
      clearDraft(scope, entityId)
      return
    }

    draftFlow.draftSavedByThisHook = draft
    setDraft(scope, entityId, draft)
  }, [clearDraft, draft, enabled, entityId, hasContent, initSignature, scope, setDraft])

  const clearCurrentDraft = useCallback(() => {
    clearDraft(scope, entityId)
  }, [clearDraft, entityId, scope])

  const clearCurrentJobs = useCallback(() => {
    clearEntityJobs(scope, entityId)
  }, [clearEntityJobs, entityId, scope])

  return {
    clearCurrentDraft,
    clearCurrentJobs,
  }
}
